import { isSendableEmailStatus, isTerminalEmailStatus } from "./email-status";

/**
 * The list settings that decide which contact lookups a person the list adds gets (the API's
 * `GetEmailInformation`, `GetPhoneInformation`, `GetContactInformation`, read as their values).
 */
export interface ContactLookupSettings {
	getEmailInformation?: string | null;
	getPhoneInformation?: string | null;
	getContactInformation?: string | null;
}

/** When a list looks up the email and phone of a person's company, if at all. */
export const CompanyContactLookup = {
	OFF: "OFF",
	/** For every person added whose company has neither yet. */
	ALWAYS: "ALWAYS",
	/** Only for a person added with an email that is not valid. */
	WHEN_NO_VALID_EMAIL: "WHEN_NO_VALID_EMAIL",
} as const;

export type CompanyContactLookup = (typeof CompanyContactLookup)[keyof typeof CompanyContactLookup];

/**
 * Which of a person's own lookups the list runs when it adds them ("Try to find email", "Try to
 * find phone"). The API runs and charges them on this answer, and the bulk Find person dialog and
 * the tracker step quote it with the prices beside it in `credit-prices.ts` (PREC-4398, PREC-4570).
 */
export function personContactLookupsOf(
	list: Pick<ContactLookupSettings, "getEmailInformation" | "getPhoneInformation">
): { email: boolean; phone: boolean } {
	return {
		email: list.getEmailInformation === "TRY_TO_FIND_EMAIL",
		phone: list.getPhoneInformation === "TRY_TO_FIND_PHONE",
	};
}

/** The list's company contact lookup setting, as the API runs it and the web quotes it. */
export function companyContactLookupOf(
	list: Pick<ContactLookupSettings, "getContactInformation">
): CompanyContactLookup {
	switch (list.getContactInformation) {
		case "ALWAYS":
			return CompanyContactLookup.ALWAYS;
		case "ONLY_WHEN_NO_EMAIL_FOUND":
			return CompanyContactLookup.WHEN_NO_VALID_EMAIL;
		default:
			return CompanyContactLookup.OFF;
	}
}

/** A prospect's email as a Contacts email lookup reads it. */
export interface EmailLookupHolder {
	email?: string | null;
	emailStatus?: string | null;
}

/** A prospect's phone as a Contacts phone lookup reads it. */
export interface PhoneLookupHolder {
	phone?: string | null;
	phoneStatus?: string | null;
}

/**
 * Whether a Contacts (or MCP) email lookup leaves the prospect alone because it already holds a
 * result: a sendable address (VALID or RISKY, PREC-4265), or a terminal status (an earlier search found none, a mail bounced).
 * The API's `EMAIL_LOOKUP.isSettled` (`contact-lookup-request.util.ts`) runs the lookup on it, and
 * the Contacts credit confirm (`useContactLookups`) counts the contacts it searches and skips with
 * it, so the dialog cannot quote a contact the lookup skips (PREC-4263).
 */
export const isEmailLookupSettled = (prospect: EmailLookupHolder): boolean =>
	isTerminalEmailStatus(prospect.emailStatus) ||
	(!!prospect.email && isSendableEmailStatus(prospect.emailStatus));

/**
 * Whether a Contacts (or MCP) phone lookup leaves the prospect alone: a valid number, or an
 * earlier search that found none. Run by the API's `PHONE_LOOKUP.isSettled` and counted by the
 * Contacts credit confirm, like `isEmailLookupSettled` (PREC-4263).
 */
export const isPhoneLookupSettled = (prospect: PhoneLookupHolder): boolean =>
	prospect.phoneStatus === "INVALID" || (!!prospect.phone && prospect.phoneStatus === "VALID");
